import type { BalancesByCurrencyMarketAccountID } from '@src/containers/Transfers/transferOptionUtils';
import {
  getEmptyTransferForm,
  getTransferAccountsCompatibilityError,
  type TransferForm,
} from '@src/containers/Transfers/types';
import type { useChainCurrencyOptions } from '@src/containers/Transfers/useTransferFormOptions';
import { getTypedKeys, useCurrency } from '@talos/kyoko';
import Big from 'big.js';
import { useEffect, useMemo, useState } from 'react';

type TransferFormDisabled = {
  [key in keyof TransferForm]: boolean;
};

export interface TransferFormErrors {
  balances: string;
  chainCurrency: string;
  provider: string;
  asset: string;
  amount: string;
  source: string;
  destination: string;
}

export const useTransferFormValidation = (
  form: TransferForm,
  allInputsDisabled: boolean,
  chainCurrencyOptions: ReturnType<typeof useChainCurrencyOptions>,
  availableSourceAmount?: string,
  balances?: BalancesByCurrencyMarketAccountID
) => {
  const { asset, destination, provider, source, chainCurrency, amount } = form;
  const currencyInfo = useCurrency(asset);
  const hasRequiredBalanceFields = provider !== '' && asset !== '' && source != null && destination != null;

  const [disabledInputs, setDisabledInputs] = useState<Partial<TransferFormDisabled>>({});
  useEffect(() => {
    const disabled: Partial<TransferFormDisabled> = {};
    if (allInputsDisabled) {
      // Disable all inputs in the UI
      getTypedKeys(getEmptyTransferForm()).forEach(key => {
        disabled[key] = true;
      });
    } else {
      if (provider === '') {
        disabled.asset = true;
        disabled.amount = true;
      }
      if (asset === '' || disabled.asset) {
        disabled.source = true;
        disabled.destination = true;
      }
      if (source === undefined || destination === undefined) {
        disabled.description = true;
        disabled.reference = true;
      }
      if (provider === 'fireblocks') {
        disabled.reference = true;
      }
    }
    setDisabledInputs(disabled);
  }, [allInputsDisabled, asset, destination, provider, source]);

  // Derive synchronously so selecting a value cannot briefly show a stale required error.
  const errors = useMemo(() => {
    const newErrors: Partial<TransferFormErrors> = {};
    if (hasRequiredBalanceFields && !balances) {
      newErrors.balances = 'Balances are unavailable. Contact Talos Support.';
    }

    if (!provider) {
      newErrors.provider = 'Provider is required';
    }
    if (!asset) {
      newErrors.asset = 'Asset is required';
    }
    if (!amount) {
      newErrors.amount = 'Amount is required';
    } else {
      const bigAmount = Big(amount ?? '0');
      if (bigAmount.lt(currencyInfo?.MinIncrement ?? 0)) {
        newErrors.amount = `Amount must be greater than or equal to ${currencyInfo?.MinIncrement ?? 0}`;
      } else if (bigAmount.lte(0)) {
        newErrors.amount = 'Amount must be greater than 0';
      } else if (!allInputsDisabled && bigAmount.gt(availableSourceAmount || 0) && source) {
        // Skip while submitting/loading: balance can drop before the form clears,
        // which would falsely show amount>available for an in-flight transfer.
        newErrors.amount = 'Amount is greater than available amount';
      }
    }

    if (!source) {
      newErrors.source = 'Source is required';
    }

    if (!destination) {
      newErrors.destination = 'Destination is required';
    }

    const destinationAccountError = getTransferAccountsCompatibilityError({ source, destination });
    if (destinationAccountError) {
      newErrors.destination = destinationAccountError;
    }

    if (chainCurrencyOptions.required && !chainCurrency) {
      newErrors.chainCurrency = 'Chain is required';
    }

    return newErrors;
  }, [
    allInputsDisabled,
    amount,
    asset,
    destination,
    provider,
    source,
    balances,
    hasRequiredBalanceFields,
    availableSourceAmount,
    currencyInfo?.MinIncrement,
    chainCurrency,
    chainCurrencyOptions.required,
  ]);

  return {
    disabledInputs,
    errors,
  };
};
